import { useState, useMemo, useCallback } from 'react';
import { ChevronDownIcon, ExclamationTriangleIcon, ArrowPathIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../shared/OathTableComponents';

const AGING_BUCKETS = [
  { key: 'Current',    label: 'Current',    color: 'bg-green-500',  textCls: 'text-green-700 dark:text-green-400', bgCls: 'bg-green-50 dark:bg-green-900/20' },
  { key: '30-60 Days', label: '30-60 Days', color: 'bg-yellow-500', textCls: 'text-yellow-700 dark:text-yellow-400', bgCls: 'bg-yellow-50 dark:bg-yellow-900/20' },
  { key: '60-90 Days', label: '60-90 Days', color: 'bg-orange-500', textCls: 'text-orange-700 dark:text-orange-400', bgCls: 'bg-orange-50 dark:bg-orange-900/20' },
  { key: '90+ Days',   label: '90+ Days',   color: 'bg-red-500',    textCls: 'text-red-700 dark:text-red-400', bgCls: 'bg-red-50 dark:bg-red-900/20', isAlert: true },
];

const ALL_BUCKETS = [
  ...AGING_BUCKETS,
  { key: 'Total', label: 'Total', color: 'bg-gray-400', textCls: 'text-gray-700 dark:text-gray-300', bgCls: 'bg-gray-50 dark:bg-gray-700/20' },
];

const displayVal = (item, field) => {
  if (!item) return '\u2014';
  const displayKey = `${field}Display`;
  if (item[displayKey] != null) return item[displayKey];
  if (item[field] != null) return formatCurrency(item[field]);
  return '\u2014';
};

const isNegative = (item, field) => {
  if (!item) return false;
  return (item[field] ?? 0) < 0;
};

const getTotal = (agency) => {
  const item = agency.accountsReceivable?.find((x) => x.description === 'Total');
  return item?.total ?? 0;
};

const has90PlusBalance = (agency) => {
  const item = agency.accountsReceivable?.find((x) => x.description === '90+ Days');
  return item && (item.net > 0 || item.misc > 0 || item.buf > 0);
};

/** Horizontal stacked bar showing age distribution */
const AgingBar = ({ agency }) => {
  const total = getTotal(agency);
  if (total <= 0) return null;

  const segments = AGING_BUCKETS.map((bucket) => {
    const item = agency.accountsReceivable?.find((x) => x.description === bucket.key);
    const val = item?.total ?? 0;
    return { ...bucket, val, pct: Math.max(0, (val / total) * 100) };
  }).filter((s) => s.val > 0);

  return (
    <div className="flex h-1.5 qhd:h-2 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 w-full">
      {segments.map((seg) => (
        <div
          key={seg.key}
          className={`${seg.color} transition-all`}
          style={{ width: `${seg.pct}%` }}
          title={`${seg.label}: ${formatCurrency(seg.val)}`}
        />
      ))}
    </div>
  );
};

/** Expanded detail panel for one agency */
const AgencyDetail = ({ agency }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 qhd:gap-3">
    {ALL_BUCKETS.map((bucket) => {
      const item = agency.accountsReceivable?.find((x) => x.description === bucket.key);
      const isAlert = bucket.isAlert && item && (item.net > 0 || item.misc > 0 || item.buf > 0);
      const isTotal = bucket.key === 'Total';

      return (
        <div
          key={bucket.key}
          className={`rounded-lg px-3 py-2 qhd:px-4 qhd:py-3 ${bucket.bgCls} ${isTotal ? 'sm:col-span-2 lg:col-span-1 ring-1 ring-gray-200 dark:ring-gray-600' : ''}`}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${bucket.color} flex-shrink-0`} />
            <span className={`text-[10px] qhd:text-xs font-semibold uppercase tracking-wide ${bucket.textCls}`}>
              {bucket.label}
            </span>
            {isAlert && <ExclamationTriangleIcon className="h-3 w-3 text-red-500" />}
          </div>
          <div className="space-y-0.5">
            {[{ key: 'net', label: 'Net Due' }, { key: 'misc', label: 'Misc' }, { key: 'buf', label: 'BUF' }].map(({ key: field, label }) => {
              const credit = isNegative(item, field);
              return (
                <div key={field} className="flex items-center justify-between gap-2">
                  <span className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 uppercase">{label}</span>
                  <span className={`text-xs qhd:text-sm tabular-nums font-medium ${
                    credit ? 'text-green-600 dark:text-green-400 font-semibold'
                    : isAlert ? 'text-red-700 dark:text-red-400 font-semibold'
                    : isTotal ? 'text-gray-900 dark:text-white font-semibold'
                    : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {displayVal(item, field)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

const SORT_OPTIONS = [
  { label: 'Name', field: 'agencyName' },
  { label: 'Total AR', field: 'total' },
  { label: '90+ Days', field: '90+' },
];

const SubAgencyARTable = ({ subAgencies = [], totals, onRefresh, onExport, loading }) => {
  const [sortField, setSortField] = useState('total');
  const [sortDesc, setSortDesc] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDesc((d) => !d);
    } else {
      setSortField(field);
      setSortDesc(field !== 'agencyName');
    }
  };

  const toggleExpand = useCallback((agencyId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(agencyId)) {
        next.delete(agencyId);
      } else {
        next.add(agencyId);
      }
      return next;
    });
  }, []);

  const sorted = useMemo(() => {
    let list = subAgencies;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((a) =>
        (a.agencyName ?? '').toLowerCase().includes(q) ||
        String(a.agencyId).includes(q)
      );
    }
    return [...list].sort((a, b) => {
      if (sortField === 'agencyName') {
        const cmp = (a.agencyName ?? '').toLowerCase().localeCompare((b.agencyName ?? '').toLowerCase());
        return sortDesc ? -cmp : cmp;
      }
      const bucketKey = sortField === 'total' ? 'Total' : '90+ Days';
      const aVal = a.accountsReceivable?.find((x) => x.description === bucketKey)?.total ?? 0;
      const bVal = b.accountsReceivable?.find((x) => x.description === bucketKey)?.total ?? 0;
      return sortDesc ? bVal - aVal : aVal - bVal;
    });
  }, [subAgencies, sortField, sortDesc, searchQuery]);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(sorted.map((a) => a.agencyId)));
  }, [sorted]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const allExpanded = sorted.length > 0 && sorted.every((a) => expandedIds.has(a.agencyId));

  const totalAR = totals?.accountsReceivable?.find((x) => x.description === 'Total');
  const agencies90Plus = useMemo(() => subAgencies.filter(has90PlusBalance), [subAgencies]);

  return (
    <div className="space-y-3 qhd:space-y-4">
      {/* Totals summary — visually elevated above the individual cards */}
      {totals && (
        <div className="bg-white dark:bg-gray-800 rounded-xl qhd:rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-primary via-primary to-orange-500" />
          <div className="p-3 qhd:p-4">
            <div className="flex items-center justify-between mb-2 qhd:mb-3">
              <div>
                <span className="text-xs qhd:text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                  All Sub-Agencies
                </span>
                {agencies90Plus.length > 0 && (
                  <p className="text-[10px] qhd:text-xs text-red-600 dark:text-red-400 font-medium mt-0.5">
                    {agencies90Plus.length} of {subAgencies.length} with 90+ day balances
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 qhd:gap-3">
                <span className="text-lg qhd:text-xl font-bold text-gray-900 dark:text-white tabular-nums">
                  {totalAR?.totalDisplay || formatCurrency(totalAR?.total ?? 0)}
                </span>
                <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 divide-x divide-gray-200 dark:divide-gray-600 overflow-hidden">
                  {onRefresh && (
                    <button
                      onClick={onRefresh}
                      disabled={loading}
                      className="flex items-center gap-1.5 px-2 py-1 qhd:px-2.5 qhd:py-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                      title="Refresh data"
                    >
                      <ArrowPathIcon className={`h-3.5 w-3.5 qhd:h-4 qhd:w-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  )}
                  {onExport && (
                    <button
                      onClick={onExport}
                      className="flex items-center gap-1.5 px-2 py-1 qhd:px-2.5 qhd:py-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      title="Export to CSV"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5 qhd:h-4 qhd:w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex h-2.5 qhd:h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600 mb-3 qhd:mb-4">
              {(() => {
                const t = totalAR?.total ?? 0;
                if (t <= 0) return null;
                return AGING_BUCKETS.map((bucket) => {
                  const item = totals.accountsReceivable?.find((x) => x.description === bucket.key);
                  const val = item?.total ?? 0;
                  return val > 0 ? (
                    <div
                      key={bucket.key}
                      className={`${bucket.color}`}
                      style={{ width: `${(val / t) * 100}%` }}
                      title={`${bucket.label}: ${formatCurrency(val)}`}
                    />
                  ) : null;
                });
              })()}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 qhd:gap-x-6">
              {AGING_BUCKETS.map((bucket) => {
                const item = totals.accountsReceivable?.find((x) => x.description === bucket.key);
                return (
                  <div key={bucket.key} className="flex items-center gap-1.5">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${bucket.color} flex-shrink-0`} />
                    <span className={`text-[10px] qhd:text-xs font-semibold ${bucket.textCls}`}>{bucket.label}</span>
                    <span className="text-xs qhd:text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                      {item?.totalDisplay || formatCurrency(item?.total ?? 0)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Search + Sort controls + expand/collapse */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-1">
        {/* Search */}
        <div className="relative flex-shrink-0">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 qhd:h-4 qhd:w-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search agencies..."
            className="w-full sm:w-44 qhd:sm:w-52 pl-7 pr-7 qhd:pl-8 qhd:pr-8 py-1 qhd:py-1.5 text-[10px] qhd:text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-3 w-3 qhd:h-3.5 qhd:w-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center justify-between flex-1">
        <div className="flex items-center gap-2">
          {searchQuery.trim() && sorted.length > 0 && (
            <span className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 font-medium">{sorted.length} found</span>
          )}
          <span className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 font-medium">Sort:</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.field}
              onClick={() => handleSort(opt.field)}
              className={`px-2 py-0.5 qhd:px-2.5 qhd:py-1 rounded text-[10px] qhd:text-xs font-medium transition-colors ${
                sortField === opt.field
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {opt.label}
              {sortField === opt.field && (
                <span className="ml-0.5">{sortDesc ? '\u2193' : '\u2191'}</span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={allExpanded ? collapseAll : expandAll}
          className="text-[10px] qhd:text-xs text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary-light font-medium transition-colors"
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
        </div>
      </div>

      {/* Agency accordion cards */}
      {sorted.length === 0 && searchQuery.trim() ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">No agencies match &ldquo;{searchQuery.trim()}&rdquo;</p>
        </div>
      ) : (
      <div className="space-y-2 qhd:space-y-2.5">
        {sorted.map((agency) => {
          const total = getTotal(agency);
          const isAlert = has90PlusBalance(agency);
          const totalItem = agency.accountsReceivable?.find((x) => x.description === 'Total');
          const isOpen = expandedIds.has(agency.agencyId);

          return (
            <div
              key={agency.agencyId}
              className={`bg-white dark:bg-gray-800 rounded-xl qhd:rounded-2xl shadow-sm border transition-colors ${
                isAlert
                  ? 'border-red-200 dark:border-red-800/50'
                  : 'border-gray-200 dark:border-gray-700'
              } ${isOpen ? 'ring-1 ring-primary/20 dark:ring-primary/30' : ''}`}
            >
              <button
                onClick={() => toggleExpand(agency.agencyId)}
                className="w-full px-3 py-2.5 qhd:px-4 qhd:py-3 flex items-center gap-3 text-left group"
              >
                {/* Alert indicator */}
                {isAlert && (
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500 flex-shrink-0 animate-pulse" />
                )}

                {/* Agency info + aging bar */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <p className="text-sm qhd:text-base font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
                        {agency.agencyName}
                      </p>
                      <p className="text-[10px] qhd:text-xs text-gray-400 dark:text-gray-500">#{agency.agencyId}</p>
                    </div>
                    <span className={`text-sm qhd:text-base font-bold tabular-nums flex-shrink-0 ${
                      total < 0 ? 'text-green-600 dark:text-green-400'
                      : isAlert ? 'text-red-700 dark:text-red-400'
                      : 'text-gray-900 dark:text-white'
                    }`}>
                      {totalItem?.totalDisplay || formatCurrency(total)}
                    </span>
                  </div>
                  <AgingBar agency={agency} />
                </div>

                {/* Expand chevron */}
                <ChevronDownIcon className={`w-4 h-4 qhd:w-5 qhd:h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && (
                <div className="px-3 pb-3 qhd:px-4 qhd:pb-4 pt-0">
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-3 qhd:pt-4">
                    <AgencyDetail agency={agency} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default SubAgencyARTable;
